'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useRouter, useParams } from '@/shared/navigation'
import { DelmanPageHeader } from '@/features/delman/components/DelmanPageHeader'
import { useReceivingPoll } from '@/features/delman/hooks/useReceivingPoll'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { enableHandoverSimulation } from '@/shared/env'
import {
  saveTripRecord,
  stopBadgeClass,
  stopStatusLabel,
  type TripRecord,
} from '@/features/delman/storage/tripStorage'
import { loadTodayTripPreferApi } from '@/features/delman/api/delmanTripApi'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { Toast } from '@/shared/components/Toast'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

export function DelmanDeliveryDetailPage() {
  const { deliveryId } = useParams<{ deliveryId: string }>()
  const router = useRouter()
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null

  const [trip, setTrip] = useState<TripRecord | null>(null)
  const tripRef = useRef<TripRecord | null>(null)
  tripRef.current = trip
  const [toast, setToast] = useState<string | null>(null)

  const stop = trip?.stops.find((s) => s.id === deliveryId)

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  useEffect(() => {
    if (!hydrated || !device) return
    let cancelled = false
    void loadTodayTripPreferApi({
      delmanId: device.delmanId,
      delmanName: device.delmanName,
      kitchenName: device.kitchenName,
    }).then((loaded) => {
      if (!cancelled) setTrip(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [hydrated, device?.delmanId, device?.delmanName, device?.kitchenName])

  function persistTrip(next: TripRecord) {
    saveTripRecord(next)
    setTrip(next)
  }

  useReceivingPoll({
    enabled: hydrated && trip?.status === 'in_progress',
    getTrip: () => tripRef.current,
    onTripUpdate: persistTrip,
    onStoreScanned: (event) => {
      if (event.deliveryId === deliveryId) {
        showToast(delmanTerms.handover.storeScannedToast(event.storeName))
      }
    },
    onStoreCompleted: (event) => {
      if (event.deliveryId === deliveryId) {
        showToast(delmanTerms.handover.storeCompletedToast(event.storeName))
      }
    },
  })

  if (!hydrated) {
    return <div className="min-h-[200px]" aria-hidden />
  }

  if (!stop) {
    return (
      <section className="card space-y-3">
        <p className="text-body-sm text-status-danger">{delmanTerms.delivery.notFound}</p>
        <Link href="/delman/trip" className="btn-outline block text-center min-h-[44px] leading-[44px]">
          {delmanTerms.delivery.backToTrip}
        </Link>
      </section>
    )
  }

  const totalQty = stop.lines.reduce((sum, line) => sum + line.qtyShipped, 0)

  function simulateStoreCompleted() {
    if (!enableHandoverSimulation || !stop || !trip) return
    const nextStops = trip.stops.map((s) =>
      s.id === stop.id
        ? {
            ...s,
            status: 'receiving_completed' as const,
            receivingStatus: 'completed' as const,
            lines: s.lines.map((line) => ({ ...line, qtyReceived: line.qtyShipped })),
          }
        : s,
    )
    persistTrip({ ...trip, stops: nextStops })
    router.push('/delman/trip')
  }

  function receivingStatusText(s: NonNullable<typeof stop>): string {
    if (s.receivingStatus === 'draft') return delmanTerms.delivery.receivingDraft
    if (s.status === 'qr_shown') return delmanTerms.delivery.receivingWaiting
    return delmanTerms.delivery.receivingNone
  }

  return (
    <section className="space-y-4 pb-4">
      <div className="card space-y-4">
        <DelmanPageHeader
          backHref="/delman/trip"
          backLabel={delmanTerms.delivery.backToTrip}
          meta={stop.id}
          title={stop.storeName}
          badge={<span className={stopBadgeClass(stop)}>{stopStatusLabel(stop)}</span>}
        />

        {stop.receivingStatus === 'draft' && (
          <div className="flex items-center gap-2 rounded-lg bg-status-success/10 border border-status-success/30 px-3 py-2">
            <span className="material-symbols-outlined text-status-success">edit_note</span>
            <p className="text-body-sm text-status-success font-semibold">{delmanTerms.handover.storeDraft}</p>
          </div>
        )}

        <p className="text-body-sm text-on-surface-variant bg-surface-container rounded-lg px-3 py-2.5 flex items-start gap-2">
          <span className="material-symbols-outlined text-base text-primary shrink-0 mt-0.5">info</span>
          {delmanTerms.delivery.readOnly}
        </p>

        <ul className="space-y-2">
          {stop.lines.map((line) => (
            <li
              key={`${line.productName}-${line.productionDate}`}
              className="rounded-lg border border-outline-variant px-3 py-2.5"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-body-sm font-semibold text-on-surface">{line.productName}</p>
                <p className="font-mono text-label-md text-on-surface-variant shrink-0">{line.productionDate}</p>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-body-sm">
                <div className="rounded-md bg-surface-container px-2 py-1.5">
                  <p className="text-[10px] uppercase text-on-surface-variant">Kirim</p>
                  <p className="font-mono font-semibold">{line.qtyShipped}</p>
                </div>
                <div className="rounded-md bg-surface-container px-2 py-1.5">
                  <p className="text-[10px] uppercase text-on-surface-variant">Terima</p>
                  <p className="font-mono font-semibold text-on-surface-variant">
                    {line.qtyReceived ?? '—'}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <p className="text-body-sm text-on-surface-variant border-t border-surface-variant pt-3">
          {delmanTerms.delivery.totalShipped(totalQty)}
        </p>
      </div>

      {stop.receivingStatus === 'completed' && (
        <div className="card bg-status-success/10 border-status-success/30 flex items-center gap-2">
          <span className="material-symbols-outlined text-status-success icon-fill">check_circle</span>
          <p className="text-body-sm text-status-success font-semibold">{delmanTerms.delivery.completedBanner}</p>
        </div>
      )}

      {stop.receivingStatus !== 'completed' && trip && trip.status === 'in_progress' && (
        <div className="space-y-3">
          <Link
            href={`/delman/handover/${stop.id}`}
            className="btn-primary block text-center min-h-[52px] leading-[52px] btn-tactile"
          >
            {delmanTerms.delivery.showQr}
          </Link>
          <div className="card space-y-2">
            <p className="text-label-md font-semibold text-on-surface">{delmanTerms.delivery.receivingStatus}</p>
            <p className="text-body-sm">
              Status: <strong>{receivingStatusText(stop)}</strong>
            </p>
            <p className="text-label-md text-on-surface-variant">{delmanTerms.delivery.damagedNote}</p>
          </div>
        </div>
      )}

      {enableHandoverSimulation && stop.receivingStatus !== 'completed' && (
        <button type="button" className="btn-outline w-full text-xs" onClick={simulateStoreCompleted}>
          [Dev] Simulasi toko selesai terima
        </button>
      )}

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
